import { format, parseISO, isValid, formatDistance, differenceInDays } from 'date-fns'
import { fr, enUS, de, nl } from 'date-fns/locale'

const locales = {
  fr,
  en: enUS,
  de,
  nl,
}

// Default locale for Belgian French
const DEFAULT_LOCALE = 'fr'

/**
 * Universal date parser - handles both ISO and legacy MM/DD/YYYY formats
 * @param dateStr - The date string to parse (ISO or MM/DD/YYYY)
 * @returns Date object or null if invalid
 */
export function parseDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null

  // Try ISO format first (new format after migration)
  let date = parseISO(dateStr)
  if (isValid(date)) return date

  // Fallback: legacy MM/DD/YYYY format (during migration)
  const parts = dateStr.split('/')
  if (parts.length === 3) {
    const [month, day, year] = parts.map(p => parseInt(p, 10))
    if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
      date = new Date(year, month - 1, day)
      if (isValid(date)) return date
    }
  }

  // Final fallback: try native Date parsing
  date = new Date(dateStr)
  return isValid(date) ? date : null
}

/**
 * Format date for display in DD/MM/YYYY format (Belgian standard)
 * @param date - Date object or string
 * @returns Formatted date string or '-' if invalid
 */
export function formatDisplayDate(date: Date | string | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? parseDate(date) : date
  if (!d || !isValid(d)) return '-'
  return format(d, 'dd/MM/yyyy', { locale: fr })
}

/**
 * Format date and time for display in DD/MM/YYYY HH:mm format
 * @param date - Date object or string
 * @returns Formatted datetime string or '-' if invalid
 */
export function formatDisplayDateTime(date: Date | string | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? parseDate(date) : date
  if (!d || !isValid(d)) return '-'
  return format(d, 'dd/MM/yyyy HH:mm', { locale: fr })
}

/**
 * Format date as relative time (e.g., "il y a 5 minutes")
 * @param date - Date object or string
 * @returns Relative time string or '' if invalid
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return ''
  const d = typeof date === 'string' ? parseDate(date) : date
  if (!d || !isValid(d)) return ''
  return formatDistance(d, new Date(), { addSuffix: true, locale: fr })
}

/**
 * Get the number of days since a date
 * @param dateStr - The date string to compare
 * @returns Number of days or null if invalid
 */
export function getDaysSince(dateStr: string | null | undefined): number | null {
  const date = parseDate(dateStr)
  if (!date) return null
  return differenceInDays(new Date(), date)
}

/**
 * Check if a date is in the past (overdue)
 * Uses parseDate internally to handle both ISO and legacy MM/DD/YYYY formats
 * @param dateStr - The date string to check
 * @returns true if the date is in the past, false otherwise
 */
export function isOverdue(dateStr: string | null | undefined): boolean {
  const date = parseDate(dateStr)
  if (!date) return false
  return date < new Date()
}

/**
 * Format a price amount in EUR (Belgian format)
 * @param price - The price to format
 * @returns Formatted price string or '-' if invalid
 */
export function formatPrice(price?: string | number): string {
  if (price === undefined || price === null || price === '') return '-'
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(numPrice)) return '-'
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR'
  }).format(numPrice)
}

// Legacy exports for backward compatibility
export const formatDate = (
  date: Date | string,
  formatStr: string = 'dd/MM/yyyy',
  locale: string = DEFAULT_LOCALE
): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date

  if (!isValid(dateObj)) {
    return ''
  }

  return format(dateObj, formatStr, {
    locale: locales[locale as keyof typeof locales] || locales.fr,
  })
}

export const formatDateTime = (
  date: Date | string,
  locale: string = DEFAULT_LOCALE
): string => {
  return formatDate(date, 'dd/MM/yyyy HH:mm', locale)
}

export const formatRelative = (
  date: Date | string,
  locale: string = DEFAULT_LOCALE
): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date

  if (!isValid(dateObj)) {
    return ''
  }

  return formatDistance(dateObj, new Date(), {
    addSuffix: true,
    locale: locales[locale as keyof typeof locales] || locales.fr,
  })
}
